(() => {
  // ===== CONFIG =====
  const INSURANCE_PRODUCT_HANDLE = 'shipping-insurance';

  // $0.95 per $100, rounded UP
  const STEP_CENTS = 10000;      // $100 in cents
  const PREMIUM_PER_STEP = 95;   // $0.95 in cents

  // If customer removes insurance, don’t re-add for this browser
  const OPT_OUT_KEY = 'scv_shipping_insurance_opt_out';

  // Toggle debug logs
  const DEBUG = true;
  function log(...args) {
    if (DEBUG) console.log('[SCV Insurance]', ...args);
  }

  console.log('[SCV Insurance] FILE LOADED', location.pathname);

  // ===== STATE =====
  let variants = null;
  let busy = false;
  let lastSyncAt = 0;
  let hadInsuranceEver = false;

  // ===== HELPERS =====
  async function loadInsuranceVariants() {
    if (variants) return variants;

    const res = await fetch(`/products/${INSURANCE_PRODUCT_HANDLE}.js`, {
      credentials: 'same-origin',
      cache: 'no-store'
    });

    if (!res.ok) throw new Error(`Could not load insurance product JSON (status ${res.status})`);

    const data = await res.json();

    variants = (data.variants || [])
      .map(v => ({ id: v.id, price: v.price })) // cents
      .sort((a, b) => a.price - b.price);

    if (!variants.length) throw new Error('Insurance product has no variants');

    return variants;
  }

  async function getCart() {
    const r = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
    return r.json();
  }

  function isInsuranceItem(item, insuranceVariants) {
    return insuranceVariants.some(v => v.id === item.id);
  }
function isExcludedSample(item) {
  return item.url && item.url.includes('/products/stair-nosing-free-sample-vinyl-laminate-evp');
}
  function subtotalExcludingInsurance(cart, insuranceVariants) {
    return cart.items.reduce((sum, item) => {
      if (isInsuranceItem(item, insuranceVariants)) return sum;
      return sum + (item.final_line_price || 0);
    }, 0);
  }

  function computeTargetPremiumCents(subtotalCents) {
    const steps = Math.max(1, Math.ceil(subtotalCents / STEP_CENTS));
    return steps * PREMIUM_PER_STEP;
  }

  function findVariantByPrice(insuranceVariants, targetCents) {
    const exact = insuranceVariants.find(v => v.price === targetCents);
    if (exact) return exact;

    // nearest
    let best = insuranceVariants[0];
    let bestDiff = Math.abs(best.price - targetCents);

    for (const v of insuranceVariants) {
      const diff = Math.abs(v.price - targetCents);
      if (diff < bestDiff) {
        best = v;
        bestDiff = diff;
      }
    }
    return best;
  }

  async function addVariant(variantId) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Failed to add insurance (status ${res.status}) ${txt}`);
    }
  }

  async function changeByKey(lineItemKey, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: lineItemKey, quantity })
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Failed to change cart line (status ${res.status}) ${txt}`);
    }
  }

  // ===== CORE =====
  async function syncInsurance(reason = '') {
    const now = Date.now();
    if (now - lastSyncAt < 450) return;
    lastSyncAt = now;

    if (busy) return;
    if (localStorage.getItem(OPT_OUT_KEY) === 'true') {
      log('opt-out active, skipping sync', reason);
      return;
    }

    busy = true;

    try {
      const insuranceVariants = await loadInsuranceVariants();
      const cart = await getCart();
      console.log('SCV DEBUG cart loaded', cart.items);

      const insuranceLines = cart.items.filter(i => isInsuranceItem(i, insuranceVariants));
     const hasRealItems = cart.items.some(i =>
  !isInsuranceItem(i, insuranceVariants) && !isExcludedSample(i)
);
const hasOnlyExcludedItems = cart.items.length > 0 && cart.items.every(i =>
  isInsuranceItem(i, insuranceVariants) || isExcludedSample(i)
);

console.log('SCV DEBUG cart items =', cart.items);

if (hasOnlyExcludedItems) {
  for (const line of insuranceLines) {
    await changeByKey(line.key, 0);
  }
  return;
}

      if (insuranceLines.length > 0) hadInsuranceEver = true;

      if (!hasRealItems) {
        // remove stranded insurance
        for (const line of insuranceLines) {
          await changeByKey(line.key, 0);
        }
        return;
      }

      const subtotal = subtotalExcludingInsurance(cart, insuranceVariants);
      const targetPremium = computeTargetPremiumCents(subtotal);
      const desiredVariant = findVariantByPrice(insuranceVariants, targetPremium);

      // remove extra insurance lines
      for (let i = 1; i < insuranceLines.length; i++) {
        await changeByKey(insuranceLines[i].key, 0);
      }

      const existing = insuranceLines[0];

      if (!existing) {
        log('Adding insurance', { reason, targetPremium, desiredVariant });
        await addVariant(desiredVariant.id);
        return;
      }

      if (existing.id !== desiredVariant.id) {
        log('Replacing insurance tier', { reason, from: existing.id, to: desiredVariant.id });
        await changeByKey(existing.key, 0);
        await addVariant(desiredVariant.id);
        return;
      }

      if (existing.quantity !== 1) {
        await changeByKey(existing.key, 1);
      }
    } catch (e) {
      console.warn('[SCV Insurance] sync error:', e, 'reason:', reason);
    } finally {
      busy = false;
    }
  }

  async function ensureInsuranceBeforeCheckout(reason = 'pre-checkout') {
    const prev = lastSyncAt;
    lastSyncAt = 0;
    try {
      await syncInsurance(reason);
    } finally {
      lastSyncAt = prev;
    }
  }

  function scheduleSyncBurst(reason) {
    setTimeout(() => syncInsurance(`${reason}-t250`), 250);
    setTimeout(() => syncInsurance(`${reason}-t900`), 900);
    setTimeout(() => syncInsurance(`${reason}-t1800`), 1800);
  }

  function installCheckoutGate() {
    let checkoutLock = false;

    document.addEventListener('click', (e) => {
      const el = e.target.closest(
        '#CartDrawer-Checkout, form[action^="/cart"] [name="checkout"], a[href^="/checkout"]'
      );
      if (!el) return;

      e.preventDefault();
      e.stopPropagation();

      if (checkoutLock) return;
      checkoutLock = true;

      ensureInsuranceBeforeCheckout('checkout-click')
        .catch(err => console.warn('[SCV Insurance] pre-checkout error:', err))
        .finally(() => {
          const href = el.getAttribute?.('href');
          if (href && href.startsWith('/checkout')) {
            window.location.assign('/checkout');
          } else {
            const form = el.closest('form');
            if (form) form.submit();
            else window.location.assign('/checkout');
          }
          checkoutLock = false;
        });
    }, true);

    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;

      const action = form.getAttribute('action') || '';
      if (!action.includes('/cart/add')) return;

      const fd = new FormData(form);
      const returnTo = (fd.get('return_to') || '').toString();
      if (!returnTo.includes('/checkout')) return;

      e.preventDefault();
      e.stopPropagation();

      if (checkoutLock) return;
      checkoutLock = true;

      fetch('/cart/add.js', { method: 'POST', body: fd, credentials: 'same-origin' })
        .then(async (r) => {
          if (!r.ok) throw new Error('Add to cart failed');
          await ensureInsuranceBeforeCheckout('return_to_checkout');
          window.location.assign('/checkout');
        })
        .catch((err) => {
          console.warn('[SCV Insurance] add→checkout gate error:', err);
          window.location.assign('/checkout');
        })
        .finally(() => {
          checkoutLock = false;
        });
    }, true);
  }

  function attachTriggers() {
    document.addEventListener('DOMContentLoaded', () => {
      log('init', location.pathname);
      syncInsurance('dom');
      scheduleSyncBurst('dom-burst');
      installCheckoutGate();
      if (location.pathname === '/cart') scheduleSyncBurst('cart-load');
    });

    document.addEventListener('click', (e) => {
      const addBtn =
        e.target.closest('button[name="add"]') ||
        e.target.closest('button[type="submit"][name="add"]') ||
        e.target.closest('button[type="submit"][form*="product-form"]');

      if (!addBtn) return;
      scheduleSyncBurst('add-click');
    });

    document.addEventListener('change', (e) => {
      if (e.target && e.target.name === 'updates[]') {
        scheduleSyncBurst('qty-change');
      }
    });

    // watchdog
    let ticks = 0;
    const iv = setInterval(() => {
      ticks += 1;
      syncInsurance('watchdog');
      if (ticks >= 10) clearInterval(iv);
    }, 2000);
  }

  attachTriggers();
})();